import React, { createContext, useContext, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';

interface OktaAuthContextType {
  token: string;
  userRoles: string[];
  isLoggedIn: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isUser: (userRole: string) => boolean;
}

const OktaAuthContext = createContext<OktaAuthContextType | undefined>(undefined);

interface OktaAuthContextProviderProps {
  children: ReactNode;
}

export const OktaAuthContextProvider: React.FC<OktaAuthContextProviderProps> = ({ children }) => {
  const { oktaAuth, authState } = useOktaAuth();

  const login = async () => {
    await oktaAuth.signInWithRedirect();
  };

  const logout = async () => {
    try {
      // Essayer d'abord la déconnexion normale
      await oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin });
    } catch (error) {
      console.warn('Erreur lors de la déconnexion Okta:', error);
      // En cas d'erreur CORS, nettoyer localement et rediriger
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = window.location.origin;
    }
  };

  const isUser = (userRole: string) => {
    // Logique pour vérifier les rôles depuis les claims du token
    const roles = authState?.accessToken?.claims?.roles;
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    return false;
  };

  const contextValue: OktaAuthContextType = {
    token: authState?.accessToken?.accessToken || '',
    userRoles: Array.isArray(authState?.accessToken?.claims?.roles) ? authState.accessToken.claims.roles.map((role) => String(role)) : [],
    isLoggedIn: authState?.isAuthenticated || false,
    login,
    logout,
    isUser,
  };

  return <OktaAuthContext.Provider value={contextValue}>{children}</OktaAuthContext.Provider>;
};

export const useOktaAuthContext = () => {
  const context = useContext(OktaAuthContext);
  if (!context) {
    throw new Error('useOktaAuthContext must be used within OktaAuthContextProvider');
  }
  return context;
};
